import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db, logout } from "./config/firebase";
import FooterNav from "./components/FooterNav";
import PageHeader from "./components/PageHeader";
import "./Profile.css";
import LoadingScreen from "./components/LoadingScreen";
import { Table, Card, Button, Form, Alert } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPen, faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import ProfileAuthEditModal from "./components/ProfileAuthEditModal";
import ProfileDeleteCourseModal from "./components/ProfileDeleteCourseModal";
import { UserData, FirebaseUser } from "./types/firebase";
import { doc, getDoc, setDoc } from 'firebase/firestore';
import type { User } from "firebase/auth";
import {
    BOOKING_CONTACT_REQUIRED_MESSAGE,
    BOOKING_NAME_REQUIRED_MESSAGE,
    hasBookingContactInfo,
    hasBookingNameFields,
} from './utils/bookingContactRequirements';

function authProviderFromUser(user: User | null | undefined): string {
    const p = user?.providerData?.[0]?.providerId;
    if (p === 'password') return 'email';
    if (p === 'google.com') return 'google';
    return p || 'unknown';
}

function Profile() {
    const [user, loading] = useAuthState(auth as any);
    const courseID = localStorage.getItem("course");
    const [coursesData, setCoursesData] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const bookingContactMessage = (location.state as { bookingContactMessage?: string } | null)
        ?.bookingContactMessage;
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showDeleteCourseModal, setShowDeleteCourseModal] = useState(false);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [courseToDelete, setCourseToDelete] = useState(null);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const profileStubAttemptedForUid = useRef<string | null>(null);

    const showProfileAuthEditModal = () => {
        setShowAuthModal(true);
    }

    const refreshUserData = useCallback(async () => {
        if (!user?.uid) return;
        const userRef = doc(db, 'users', user.uid);
        try {
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                const d = userDoc.data() as UserData;
                setUserData(d);
                setFirstName((d.firstName || '').trim());
                setLastName((d.lastName || '').trim());
                setEmail(d.email || user.email || '');
                setPhone(d.phone || '');
            } else {
                setUserData(null);
                setFirstName('');
                setLastName('');
                setEmail(user.email || '');
                setPhone('');
            }
            setUserEmail(user.email ?? null);
        } catch (err) {
            console.error("Error loading user profile:", err);
            setError("Failed to load user data");
        }
    }, [user]);

    useEffect(() => {
        void refreshUserData();
    }, [refreshUserData]);

    /** One-time minimal Firestore doc so the account row exists before the user taps Update (merge-safe). */
    useEffect(() => {
        if (!user?.uid) return;
        let cancelled = false;
        const uid = user.uid;

        const run = async () => {
            if (profileStubAttemptedForUid.current === uid) return;
            try {
                const userRef = doc(db, 'users', uid);
                const snap = await getDoc(userRef);
                if (cancelled) return;
                if (snap.exists()) {
                    profileStubAttemptedForUid.current = uid;
                    return;
                }
                const groupId = localStorage.getItem('group');
                const courseId = localStorage.getItem('course');
                const courses =
                    groupId && courseId ? [{ group: groupId, course: courseId }] : [];
                const stubEmail = user.email || '';
                await setDoc(
                    userRef,
                    {
                        uid,
                        email: stubEmail,
                        phone: '',
                        firstName: '',
                        lastName: '',
                        authProvider: authProviderFromUser(user),
                        courses,
                    },
                    { merge: true }
                );
                if (!cancelled) {
                    await refreshUserData();
                    profileStubAttemptedForUid.current = uid;
                }
            } catch (err) {
                console.error('[TRWidget] profile stub write failed', err);
                profileStubAttemptedForUid.current = null;
            }
        };

        void run();
        return () => {
            cancelled = true;
        };
    }, [user, refreshUserData]);

    // Get courses data for user
    useEffect(() => {
        const fetchCoursesData = async () => {
            if (userData?.courses && userData.courses.length > 0) {
                const pushData = [];

                for (const course of userData.courses) {
                    try {
                        const courseRef = doc(db, 'groups', course.group, 'courses', course.course);
                        const courseDoc = await getDoc(courseRef);

                        if (courseDoc.exists()) {
                            const newData = courseDoc.data();
                            newData.id = course.course;
                            newData.group = course.group;
                            pushData.push(newData);
                            setCoursesData([...pushData]);
                        }
                    } catch (err) {
                        console.error("Error fetching course data: ", err);
                    }
                }
            }
        };

        fetchCoursesData();
    }, [userData]);

    useEffect(() => {
        if (loading) {
            return;
        }
        if (!user) {
            navigate("/page/login", { replace: true });
        }
    }, [user, loading, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!user) return;

        const trimmedEmail = email.trim();
        const trimmedPhone = phone.trim();
        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();
        if (!hasBookingContactInfo(user, { email: trimmedEmail, phone: trimmedPhone })) {
            setError(BOOKING_CONTACT_REQUIRED_MESSAGE);
            return;
        }
        if (!hasBookingNameFields({ firstName: trimmedFirstName, lastName: trimmedLastName })) {
            setError(BOOKING_NAME_REQUIRED_MESSAGE);
            return;
        }

        try {
            const userRef = doc(db, "users", user.uid);
            const snap = await getDoc(userRef);
            const payload: Record<string, unknown> = {
                uid: user.uid,
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                email: trimmedEmail,
                phone: trimmedPhone,
                authProvider: authProviderFromUser(user),
                updatedAt: new Date(),
            };
            const groupId = localStorage.getItem('group');
            const courseId = localStorage.getItem('course');
            if (!snap.exists() && groupId && courseId) {
                payload.courses = [{ group: groupId, course: courseId }];
            }
            await setDoc(userRef, payload, { merge: true });
            await refreshUserData();
            setSuccess("Profile saved successfully!");
        } catch (err) {
            console.error("Error updating profile:", err);
            setError("Failed to update profile");
        }
    };

    const switchCourse = (newCourse, newGroup) => {
        if (newCourse && newGroup) {
            localStorage.setItem("group", newGroup);
            localStorage.setItem("course", newCourse);
            navigate("/page/profile", { replace: true });
        } else {
            return console.log("No course switch because the required info was not available.");
        }
    }

    const deleteCourse = (courseToDelete, groupToDelete, courseName) => {

        if (courseToDelete && groupToDelete) {
            setCourseToDelete({ name: courseName, course: courseToDelete, group: groupToDelete });
            setShowDeleteCourseModal(true);
        } else {
            return console.log("No course switch because the required info was not available.");
        }
    }

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <div className="appContainer">
            <PageHeader pageTitle='My Account' />

            <div className="px-3 maxWidth800">
                {bookingContactMessage && (
                    <Alert variant="warning" className="mt-3">
                        {bookingContactMessage}
                    </Alert>
                )}
                {user?.uid && (
                    <div className="mt-3 mb-3 text-center">
                        <span className="profile-uid-badge font-monospace text-wrap text-break">
                            UID: {user.uid}
                        </span>
                    </div>
                )}
                <div className="d-flex justify-content-between mb-2 mt-2"><h2>Contact Info</h2></div>
                <Form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                        <Form.Group className="col-6 pe-1" controlId="profileFirstName">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="First name"
                                autoComplete="given-name"
                            />
                        </Form.Group>

                        <Form.Group className="col-6 ps-1" controlId="profileLastName">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Last name"
                                autoComplete="family-name"
                            />
                        </Form.Group>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="Enter your phone number"
                        />
                    </Form.Group>

                    <Button variant="primary" type="submit">
                        Update Profile
                    </Button>
                </Form>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <div className="d-flex justify-content-between mb-2 mt-4"><h2>Login Info</h2>
                    <Button onClick={showProfileAuthEditModal} className="linkNoUnderline" variant="link" size="sm"><FontAwesomeIcon className="fa-thin" icon={faPen} /> Edit</Button>
                </div>
                <Table>
                    <tbody>

                        <tr>
                            <td><small className="text-muted d-block">Email:</small></td>
                            <td className="text-end">{userEmail}</td>
                        </tr>

                        <tr>
                            <td><small className="text-muted d-block">Password:</small></td>
                            <td className="text-end text-muted">************</td>
                        </tr>

                    </tbody>
                </Table>


                {coursesData && <h2 className="mb-3 mt-4">My Courses</h2>}
                {coursesData && coursesData?.length > 0 && coursesData?.map((courseData, i) => {
                    return (
                        <Card className="mb-3" key={courseData.id}>
                            <Card.Body>

                                <Card.Title className="d-flex justify-content-between">
                                    {courseData.name}
                                    <FontAwesomeIcon onClick={() => deleteCourse(courseData.id, courseData.group, courseData.name)} className="text-danger" icon={faTrash} />
                                </Card.Title>
                                <Card.Text>
                                    {courseData.address && courseData.address}<br></br>
                                    {courseData.city && courseData?.city + ", "}
                                    {courseData.state && courseData.state + ", "}
                                    {courseData.province && courseData.province + ", "}
                                    {courseData.zip && courseData.zip}
                                    {courseData.postalcode && courseData.postalcode}
                                </Card.Text>
                                {/* {courseID === courseData.id ?
                                    <Button disabled variant="secondary">Current Course</Button>
                                    :
                                    <Button variant="primary" onClick={() => switchCourse(courseData.id, courseData.group)}>Switch to this Course</Button>
                                } */}
                            </Card.Body>
                        </Card>
                    )
                })}

                <div className="mt-4 mb-4">
                    <Button
                        variant="outline-danger"
                        className="w-100"
                        onClick={async () => {
                            await logout();
                            const groupId = localStorage.getItem('group');
                            navigate(groupId ? `/group/${encodeURIComponent(groupId)}` : '/', { replace: true });
                        }}
                    >
                        <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                        Logout
                    </Button>
                </div>
            </div>

            {showAuthModal && (
                <ProfileAuthEditModal
                    show={showAuthModal}
                    onHide={() => setShowAuthModal(false)}
                    user={user as FirebaseUser}
                    userEmail={userEmail || ""}
                    setUserEmail={setUserEmail}
                />
            )}
            <ProfileDeleteCourseModal user={user} userData={userData} refreshUserData={refreshUserData} courseToDelete={courseToDelete} setUserData={setUserData} showDeleteCourseModal={showDeleteCourseModal} setShowDeleteCourseModal={setShowDeleteCourseModal}></ProfileDeleteCourseModal>

            <FooterNav />
        </div>
    )
}

export default Profile;